/**
 * アプリケーション統一エラークラス
 *
 * 全コードで使用する共通エラー基盤。
 * API Route では statusCode を HTTP ステータスに、
 * Server Action では handleActionError() を通じて ActionResult に変換する。
 */
import * as Sentry from '@sentry/nextjs'
import { maskPii } from '@/lib/sentry/pii'

/**
 * エラーを Sentry に送信する統一関数。
 * catch ブロックで捕捉した例外を渡すこと。
 *
 * ⚠️ **常に Sentry error レベルで記録される**（`captureException` 経由）。
 * - 業務イベント・セキュリティアラート・バリデーション警告など「エラーではない事象」には使わない。
 * - 警告レベルで記録したい場合は `logger.warn()` または `Sentry.captureMessage(msg, { level: 'warning' })` を使う。
 * - `logError(new Error('業務ログ'), ctx)` パターンは `observability/no-new-error-in-log-error` ルールで禁止。
 *
 * @param err  catch で捕捉した例外（unknown を渡してよい）
 * @param context  どこで発生したかを示す文字列（例: 'customer update sync'）
 */
export function logError(err: unknown, context: string): void {
  Sentry.captureException(err, { extra: { context: maskPii(context) ?? context } })
}

/**
 * Server Action / API Route 専用: isTransientDbError を使った詳細ログ
 *
 * 使用方法: lib/db/db-retry.ts で isTransientDbError を判定した上で、
 * 一時的でないエラーを logError で送信する。
 *
 * 例:
 *   const { isTransientDbError } = await import('@/lib/db/db-retry')
 *   if (!isTransientDbError(err)) {
 *     logError(err, 'customer update sync')
 *   }
 */
export function logErrorWithContext(err: unknown, context: string, isTransient: boolean): void {
  const safeContext = maskPii(context) ?? context
  if (isTransient) {
    // 一時的な DB エラーは警告として記録（本番では自動復旧が期待される）
    Sentry.captureMessage(
      `Transient DB error: ${err instanceof Error ? err.message : String(err)} (context: ${safeContext})`,
      'warning'
    )
  } else {
    // その他のエラーは例外として送信
    Sentry.captureException(err, { extra: { context: safeContext } })
  }
}

export class AppError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly statusCode: number
  ) {
    super(message)
    this.name = 'AppError'
    // V8 スタックトレースのキャプチャ（Node.js 環境）
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor)
    }
  }
}

export class NotFoundError extends AppError {
  constructor(resource: string, id?: string) {
    const message = id ? `${resource}（ID: ${id}）が見つかりません` : `${resource}が見つかりません`
    super(message, 'NOT_FOUND', 404)
    this.name = 'NotFoundError'
  }
}

export class ValidationError extends AppError {
  constructor(
    message: string,
    public readonly fields?: Record<string, string[]>
  ) {
    super(message, 'VALIDATION_ERROR', 400)
    this.name = 'ValidationError'
  }
}

export class UnauthorizedError extends AppError {
  constructor(message = '認証が必要です') {
    super(message, 'UNAUTHORIZED', 401)
    this.name = 'UnauthorizedError'
  }
}

export class ConflictError extends AppError {
  constructor(message: string) {
    super(message, 'CONFLICT', 409)
    this.name = 'ConflictError'
  }
}

/**
 * 未登録の種別キー（例: LINE メッセージテンプレートの messageType）で
 * 「そのキー専用の必須データ定義」を引こうとしたときに投げる。
 *
 * `?? []` / `?? defaultValue` のような黙示フォールバックで「未登録＝制約なし」と
 * 誤認させないためのガード（ADV-M14・2026-08-09）。呼び出し側は
 * 業務エラーとして保存拒否 (fail) に変換する。
 */
export class UnknownMessageTypeError extends AppError {
  constructor(messageType: string, context: string) {
    super(
      `未登録の種別「${messageType}」です（${context}）。事前に必須項目を定義してください`,
      'UNKNOWN_MESSAGE_TYPE',
      400
    )
    this.name = 'UnknownMessageTypeError'
  }
}

/**
 * 外部依存（upstream API・外部サービス）の障害。§67 Error Model が求める7区分
 * （docs/依頼正本/2026-08-12_全体構造改革・完全リファクタリング.md:1716-1728）のうち
 * 'dependency failure' に対応する（REQ-dc99160495・W03実測2026-08-15で欠落確認）。
 *
 * 自社インフラの障害（InfrastructureFailureError）と区別する。依存先起因はリトライ・
 * フォールバック・依存先への問い合わせで解決するため、自社インフラ障害とは初動対応が異なる。
 */
export class DependencyFailureError extends AppError {
  constructor(dependencyName: string) {
    super(`外部依存「${dependencyName}」でエラーが発生しました`, 'DEPENDENCY_FAILURE', 502)
    this.name = 'DependencyFailureError'
  }
}

/**
 * 自社インフラ（DB接続・ストレージ・内部ネットワーク等）の障害。§67 が求める7区分のうち
 * 'infrastructure failure' に対応する（REQ-dc99160495・W03実測2026-08-15で欠落確認）。
 *
 * DependencyFailureError（外部依存起因）とは初動対応が異なるため分離する。
 */
export class InfrastructureFailureError extends AppError {
  constructor(message: string) {
    super(message, 'INFRASTRUCTURE_FAILURE', 503)
    this.name = 'InfrastructureFailureError'
  }
}

/**
 * §67 Error Model の7区分の定義正本（HQ裁定・2026-08-18・REQ-dc99160495）。
 *
 * `lib/api/error-category.ts` の `categorizeApiErrorStatus`（app/api 配下1387箇所の
 * apiError() レスポンスへ additive に category を付与するHTTPレスポンス層・W48実装）が、
 * この配列を独立定義せず import して参照する。7区分の語彙を2箇所で個別に持つと、
 * 将来どちらかだけが更新されて食い違う（本ファイルの DependencyFailureError/
 * InfrastructureFailureError の status(502/503) とレスポンス層の status→category
 * 対応は既に整合済み・2026-08-18 W48/bd 相互確認）。
 */
export const API_ERROR_CATEGORIES = [
  'validation_error',
  'authorization_error',
  'not_found',
  'conflict',
  'dependency_failure',
  'infrastructure_failure',
  'unexpected_error',
] as const

export type ApiErrorCategory = (typeof API_ERROR_CATEGORIES)[number]
